import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  TextInput,
  Group,
  Button,
  Badge,
  Menu,
  ActionIcon,
  Stack,
  Select,
  Pagination,
  Avatar,
  Chip,
  Tooltip,
  Modal,
  Skeleton,
  Divider,
  Textarea,
} from "@mantine/core";
import {
  IconSearch,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconBan,
  IconCheck,
  IconMail,
  IconFilter,
  IconArrowsSort,
  IconArrowUp,
  IconArrowDown,
  IconEye,
  IconPlus,
  IconRefresh,
  IconDownload,
  IconUsers,
} from "@tabler/icons-react";
import axios from "axios";
import "./styles.css";
import { ExportModal } from "../../../components/ExportModal";
import { Endpoints } from "../../../lib/endpoints";
import { PaginatedResponse } from "../../../lib/types";
import { HttpCodes } from "../../../lib/data";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../hooks/usePerformRequest";
import PermissionGuard from "../../../components/PermissionGuard";
import { AdminPermissions, virelCore } from "../../../lib/data";

import { IUserProps } from "../../../lib/types/User";
import { NonPaginatedResponse } from "../../../lib/types";

type SortColumn =
  | "firstName"
  | "lastName"
  | "email"
  | "isActive"
  | "createdAt"
  | "lastLoginAt";

type SortDirection = "asc" | "desc";

const Users = () => {
  useEffect(() => {
    document.title = "Users | Virel Admin";
  }, []);

  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "active" | "inactive" | "pending" | "suspended" | null
  >(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortState, setSortState] = useState<{
    column: SortColumn;
    direction: SortDirection;
  }>({ column: "createdAt", direction: "desc" });
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileData, setProfileData] = useState<IUserProps | null>(null);

  const [actionProcessing, setActionProcessing] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Squad data keyed by userId
  const [squadMap, setSquadMap] = useState<Record<string, any>>({});

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editPhoneNumber, setEditPhoneNumber] = useState("");
  const [editIsEmailVerified, setEditIsEmailVerified] = useState<
    boolean | null
  >(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState<"suspend" | "delete" | null>(
    null,
  );
  const [confirmUser, setConfirmUser] = useState<IUserProps | null>(null);
  const [confirmReason, setConfirmReason] = useState("");

  // Onboarding modal state
  const [onboardModalOpen, setOnboardModalOpen] = useState(false);
  const [onboardEmail, setOnboardEmail] = useState("");
  const [onboardPhone, setOnboardPhone] = useState("");
  const [onboardFirstName, setOnboardFirstName] = useState("");
  const [onboardLastName, setOnboardLastName] = useState("");
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [onboardEmailError, setOnboardEmailError] = useState<string | null>(
    null,
  );
  const [onboardPhoneError, setOnboardPhoneError] = useState<string | null>(
    null,
  );

  const usersEndpoint = useMemo(
    () =>
      Endpoints.GetUsers({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        status: statusFilter || undefined,
        sortBy: sortState.column,
        sortDir: sortState.direction,
      }),
    [currentPage, pageSize, debouncedSearch, statusFilter, sortState],
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, reloadData } = usePerformRequest<
    PaginatedResponse<IUserProps[]>
  >({
    method: usersEndpoint.method,
    route: usersEndpoint.route,
    showError: false,
  });

  const toggleSort = (column: SortColumn) => {
    setSortState((prev) => {
      if (prev.column === column) {
        return {
          column,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      setCurrentPage(1);
      return { column, direction: "asc" };
    });
  };

  const sortIndicator = (column: SortColumn) => {
    if (sortState.column !== column) return <IconArrowsSort size={14} />;
    return sortState.direction === "asc" ? (
      <IconArrowUp size={14} />
    ) : (
      <IconArrowDown size={14} />
    );
  };

  const users = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;

  // Fetch squad info for all users on the current page in parallel
  useEffect(() => {
    if (!users.length) return;
    const controller = new AbortController();
    const fetchSquads = async () => {
      const results = await Promise.allSettled(
        users.map((u) =>
          PerformRequest<NonPaginatedResponse<any>>({
            method: Endpoints.GetUserSquad(u.id).method,
            route: Endpoints.GetUserSquad(u.id).route,
          }).then((res) => ({ userId: u.id, data: res.data?.data ?? null }))
        )
      );
      if (controller.signal.aborted) return;
      const map: Record<string, any> = {};
      results.forEach((r) => {
        if (r.status === "fulfilled") {
          map[r.value.userId] = r.value.data;
        }
      });
      setSquadMap(map);
    };
    fetchSquads();
    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / pageSize));
  const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd =
    totalRecords === 0 ? 0 : Math.min(currentPage * pageSize, totalRecords);

  useEffect(() => {
    if (!selectedUserId) return;
    const fetchProfile = async () => {
      setProfileLoading(true);
      const endpoint = Endpoints.GetUserProfile(selectedUserId);
      const result = await PerformRequest<NonPaginatedResponse<IUserProps>>({
        method: endpoint.method,
        route: endpoint.route,
      });
      setProfileData(result.data?.data || null);
      setProfileLoading(false);
    };
    fetchProfile().catch(() => setProfileLoading(false));
  }, [selectedUserId]);

  const getStatus = (user: IUserProps) => {
    if (user.isSuspended) return "suspended";
    if (user.isEmailVerified === false) return "pending";
    return user.isActive ? "active" : "inactive";
  };

  const getStatusBadge = (
    status: "active" | "inactive" | "pending" | "suspended",
  ) => {
    const statusConfig = {
      active: { color: "green", label: "Active" },
      inactive: { color: "red", label: "Inactive" },
      pending: { color: "yellow", label: "Pending" },
      suspended: { color: "gray", label: "Suspended" },
    };
    const config =
      statusConfig[status as keyof typeof statusConfig] ||
      statusConfig.inactive;
    return (
      <Badge color={config.color} variant="light" size="sm">
        {config.label}
      </Badge>
    );
  };

  const formatDate = (value?: string | Date) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value.toString();
    return date.toLocaleDateString();
  };

  const normalizePhoneInput = (value: string): string => {
    // Remove all non-digits
    let cleaned = value.replace(/\D/g, "");

    // If starts with 234, remove it
    if (cleaned.startsWith("234")) {
      cleaned = cleaned.slice(3);
    }

    // Ensure starts with 0
    if (cleaned.length > 0 && !cleaned.startsWith("0")) {
      cleaned = "0" + cleaned;
    }

    // Limit to 11 digits (0 + 10)
    if (cleaned.length > 11) {
      cleaned = cleaned.slice(0, 11);
    }

    return cleaned;
  };

  const handlePhoneChange = (value: string) => {
    const normalized = normalizePhoneInput(value);
    setOnboardPhone(normalized);
  };

  const handleOnboard = async () => {
    setOnboardEmailError(null);
    setOnboardPhoneError(null);

    if (!onboardEmail.trim() || !onboardPhone.trim()) {
      if (!onboardEmail.trim()) {
        setOnboardEmailError("Email is required");
      }
      if (!onboardPhone.trim()) {
        setOnboardPhoneError("Phone number is required");
      }
      return;
    }

    // Ensure phone is in correct format (0XXXXXXXXXX)
    const normalizedPhone = normalizePhoneInput(onboardPhone);

    if (normalizedPhone.length !== 11) {
      setOnboardPhoneError(
        "Phone number must be 11 digits (e.g., 08012345678)",
      );
      return;
    }

    setIsOnboarding(true);
    const endpoint = Endpoints.CreateInvite();
    const r = await PerformRequest({
      method: endpoint.method,
      route: endpoint.route,
      data: {
        email: onboardEmail.trim(),
        phoneNumber: normalizedPhone,
        firstName: onboardFirstName.trim() || undefined,
        lastName: onboardLastName.trim() || undefined,
      },
    });
    setIsOnboarding(false);

    if (r.status === HttpCodes.Success || r.status === HttpCodes.Created) {
      setOnboardModalOpen(false);
      setOnboardEmail("");
      setOnboardPhone("");
      setOnboardFirstName("");
      setOnboardLastName("");
      setOnboardEmailError(null);
      setOnboardPhoneError(null);
      await reloadData();
    } else {
      // Parse error message to determine which field has the issue
      const errorMessage =
        (r.data as any)?.message || r.message || "Failed to onboard user";

      if (errorMessage.toLowerCase().includes("email")) {
        setOnboardEmailError(errorMessage);
      } else if (errorMessage.toLowerCase().includes("phone")) {
        setOnboardPhoneError(errorMessage);
      } else {
        // Generic error - show on both fields or as a general message
        setOnboardEmailError(errorMessage);
        setOnboardPhoneError(errorMessage);
      }
    }
  };

  const getSquadCell = (userId: string) => {
    // squadMap[userId] === undefined  → still loading (show skeleton)
    // squadMap[userId] === null        → no squad
    // squadMap[userId] = { squad, role, myMembershipStatus }
    if (!(userId in squadMap)) {
      return <Skeleton height={18} width={80} />;
    }
    const sd = squadMap[userId];
    if (!sd || !sd.squad) {
      return <Text size="sm" c="dimmed">—</Text>;
    }
    const name: string = sd.squad?.name ?? "Squad";
    const role: string = sd.role ?? "member";
    const status: string = sd.myMembershipStatus ?? "active";

    let color: string;
    if (role === "leader") {
      color = "green";
    } else if (status === "active") {
      color = "blue";
    } else {
      // pending_user_acceptance or pending_admin_approval
      color = "orange";
    }

    return (
      <Badge color={color} variant="light" size="sm" maw={120} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {name}
      </Badge>
    );
  };

  const rows = users.map((user) => {
    const userStatus = getStatus(user);
    const isVirelCore = user.isVirelCore || (user.email && virelCore.includes(user.email));
    return (
      <Table.Tr key={user.id} className={isVirelCore ? "virel-core-row" : ""}>
        <Table.Td>
          <Group gap="sm">
            <Avatar size="sm" color="brand" radius="sm">
              {(user.firstName || user.email || "?").charAt(0)}
            </Avatar>
            <div>
              <Text fw={500} size="sm">
                {[user.firstName, user.lastName].filter(Boolean).join(" ") ||
                  user.email}
              </Text>
              <Text size="xs" c="dimmed">
                {user.email}
              </Text>
            </div>
          </Group>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{user.email}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{user.phoneNumber || "—"}</Text>
        </Table.Td>
        <Table.Td>
          <Badge color="yellow" variant="light" size="sm">
            Level {user.level ?? 1}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Badge
            color={user.googleConnected ? "green" : "gray"}
            variant="light"
            size="sm"
          >
            {user.googleConnected ? "Connected" : "Not Connected"}
          </Badge>
        </Table.Td>
        <Table.Td>{getStatusBadge(userStatus)}</Table.Td>
        <Table.Td>{getSquadCell(user.id)}</Table.Td>
        <Table.Td>{formatDate(user.createdAt)}</Table.Td>
        <Table.Td>{formatDate(user.lastLoginAt)}</Table.Td>
        <Table.Td>
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconEye size={14} />}
                onClick={() => window.open(`/d/users/${user.id}`, "_blank")}
              >
                View Profile
              </Menu.Item>
              <Menu.Item leftSection={<IconMail size={14} />} disabled>
                Send Email
              </Menu.Item>
              <Menu.Item
                leftSection={<IconEdit size={14} />}
                onClick={async () => {
                  setProfileLoading(true);
                  const endpoint = Endpoints.GetUserProfile(user.id);
                  const result = await PerformRequest<
                    NonPaginatedResponse<IUserProps>
                  >({
                    method: endpoint.method,
                    route: endpoint.route,
                  });
                  const fetched = result.data?.data;

                  setEditUserId(user.id);
                  setEditFirstName(fetched?.firstName || "");
                  setEditLastName(fetched?.lastName || "");
                  setEditPhoneNumber(fetched?.phoneNumber || "");
                  setEditIsEmailVerified(
                    typeof fetched?.isEmailVerified === "boolean"
                      ? fetched.isEmailVerified
                      : null,
                  );
                  setEditModalOpen(true);
                  setProfileLoading(false);
                }}
              >
                Edit User
              </Menu.Item>
              <Menu.Divider />
              {userStatus === "active" ? (
                <Menu.Item
                  leftSection={<IconBan size={14} />}
                  color="red"
                  onClick={() => {
                    setConfirmUser(user);
                    setConfirmType("suspend");
                    setConfirmReason("");
                    setConfirmOpen(true);
                  }}
                >
                  Suspend User
                </Menu.Item>
              ) : (
                <Menu.Item
                  leftSection={<IconCheck size={14} />}
                  color="green"
                  onClick={async () => {
                    setActionProcessing(true);
                    const endpoint = Endpoints.ActivateUser(user.id);
                    await PerformRequest({
                      method: endpoint.method,
                      route: endpoint.route,
                    });
                    setActionProcessing(false);
                    await reloadData();
                  }}
                  disabled={actionProcessing}
                >
                  Activate User
                </Menu.Item>
              )}
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => {
                  setConfirmUser(user);
                  setConfirmType("delete");
                  setConfirmReason("");
                  setConfirmOpen(true);
                }}
              >
                Delete User
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Table.Td>
      </Table.Tr>
    );
  });

  const skeletonRows = Array.from({
    length: Math.max(3, Math.min(pageSize, 8)),
  }).map((_v, idx) => (
    <Table.Tr key={`skeleton-${idx}`}>
      <Table.Td>
        <Group gap="sm" align="center">
          <Skeleton height={32} circle />
          <div style={{ width: "60%" }}>
            <Skeleton height={10} width="80%" mb={6} />
            <Skeleton height={8} width="60%" />
          </div>
        </Group>
      </Table.Td>
      <Table.Td>
        <Skeleton height={10} width="90%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={10} width="70%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={18} width={56} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={18} width={80} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={18} width={80} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={18} width={80} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={10} width="70%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={10} width="70%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={24} width={32} />
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <PermissionGuard
      permission={AdminPermissions.MANAGE_USERS}
      fallback={
        <Container>
          <Text>Unauthorized</Text>
        </Container>
      }
    >
      <div className="users-page">
        <Container size="xl">
          <Stack gap="lg">
            <Group justify="space-between" align="flex-start">
              <div>
                <Title order={1} className="page-title">
                  Users
                </Title>
                <Text size="sm" c="dimmed" mt={4}>
                  Manage and monitor all platform users
                </Text>
              </div>
              <Group gap="sm">
                <Button
                  leftSection={<IconPlus size={16} />}
                  onClick={() => setOnboardModalOpen(true)}
                >
                  Onboard User
                </Button>
                <Button
                  leftSection={<IconDownload size={16} />}
                  variant="light"
                  onClick={() => setExportModalOpen(true)}
                >
                  Export Data
                </Button>
              </Group>
            </Group>

            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group gap="md" mb="md" className="table-toolbar">
                <TextInput
                  placeholder="Search users by name, email, or phone..."
                  leftSection={<IconSearch size={16} />}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.currentTarget.value)}
                  style={{ flex: 1 }}
                  size="md"
                />
                <Select
                  placeholder="Filter by status"
                  data={[
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                    { value: "pending", label: "Pending" },
                  ]}
                  value={statusFilter}
                  onChange={(value) => {
                    setStatusFilter(
                      value as "active" | "inactive" | "pending" | null,
                    );
                    setCurrentPage(1);
                  }}
                  clearable
                  size="md"
                  style={{ width: 180 }}
                />
                <Group gap="xs" className="quick-filters">
                  <Tooltip label="Show active users" withArrow>
                    <Chip
                      checked={statusFilter === "active"}
                      onChange={(checked) => {
                        setStatusFilter(checked ? "active" : null);
                        setCurrentPage(1);
                      }}
                      size="sm"
                      variant="light"
                    >
                      Active
                    </Chip>
                  </Tooltip>
                  <Tooltip label="Show pending users" withArrow>
                    <Chip
                      checked={statusFilter === "pending"}
                      onChange={(checked) => {
                        setStatusFilter(checked ? "pending" : null);
                        setCurrentPage(1);
                      }}
                      size="sm"
                      variant="light"
                    >
                      Pending
                    </Chip>
                  </Tooltip>
                  <Tooltip label="Show inactive users" withArrow>
                    <Chip
                      checked={statusFilter === "inactive"}
                      onChange={(checked) => {
                        setStatusFilter(checked ? "inactive" : null);
                        setCurrentPage(1);
                      }}
                      size="sm"
                      variant="light"
                    >
                      Inactive
                    </Chip>
                  </Tooltip>
                  <Tooltip label="Show suspended users" withArrow>
                    <Chip
                      checked={statusFilter === "suspended"}
                      onChange={(checked) => {
                        setStatusFilter(checked ? "suspended" : null);
                        setCurrentPage(1);
                      }}
                      size="sm"
                      variant="light"
                    >
                      Suspended
                    </Chip>
                  </Tooltip>
                </Group>
                <Group gap="xs" align="center">
                  <Text size="sm" c="dimmed">
                    List Per Page
                  </Text>
                  <Select
                    data={["5", "10", "20", "50"]}
                    value={String(pageSize)}
                    onChange={(value) => {
                      const next = Number(value) || 10;
                      setPageSize(next);
                      setCurrentPage(1);
                    }}
                    size="sm"
                    w={100}
                  />
                </Group>
              </Group>

              <div className="table-container">
                <Table
                  striped
                  highlightOnHover
                  verticalSpacing="sm"
                  className="data-grid"
                  withColumnBorders
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th
                        className="sortable"
                        onClick={() => toggleSort("firstName")}
                      >
                        <Group gap={6} align="center">
                          <span>User</span>
                          {sortIndicator("firstName")}
                        </Group>
                      </Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Phone</Table.Th>
                      <Table.Th>Level</Table.Th>
                      <Table.Th>Google</Table.Th>
                      <Table.Th
                        className="sortable"
                        onClick={() => toggleSort("isActive")}
                      >
                        <Group gap={6} align="center">
                          <span>Status</span>
                          {sortIndicator("isActive")}
                        </Group>
                      </Table.Th>
                      <Table.Th>Squad</Table.Th>
                      <Table.Th
                        className="sortable"
                        onClick={() => toggleSort("createdAt")}
                      >
                        <Group gap={6} align="center">
                          <span>Created</span>
                          {sortIndicator("createdAt")}
                        </Group>
                      </Table.Th>
                      <Table.Th
                        className="sortable"
                        onClick={() => toggleSort("lastLoginAt")}
                      >
                        <Group gap={6} align="center">
                          <span>Last Login</span>
                          {sortIndicator("lastLoginAt")}
                        </Group>
                      </Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {isLoading ? (
                      skeletonRows
                    ) : rows.length > 0 ? (
                      rows
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={10} style={{ textAlign: "center" }}>
                          <Stack align="center" gap="xs" py="xl">
                            <IconUsers size={40} stroke={1.5} color="gray" />
                            <Text c="dimmed">No users found matching criteria</Text>
                          </Stack>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </div>

              <Group justify="center" mt="md">
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => reloadData()}
                >
                  Reload Users
                </Button>
              </Group>

              {totalPages > 1 && (
                <Group justify="center" mt="md">
                  <Pagination
                    value={currentPage}
                    onChange={setCurrentPage}
                    total={totalPages}
                    size="sm"
                  />
                </Group>
              )}

              <Group
                justify="space-between"
                mt="md"
                pt="md"
                style={{ borderTop: "1px solid #e2e8f0" }}
              >
                <Text size="sm" c="dimmed">
                  Showing {rangeStart} to {rangeEnd} of {totalRecords} users
                </Text>
              </Group>
            </Paper>
          </Stack>
        </Container>
        <Modal
          opened={!!selectedUserId}
          onClose={() => {
            setSelectedUserId(null);
            setProfileData(null);
          }}
          title="User Profile"
          size="lg"
          centered
        >
          {profileLoading || !profileData ? (
            <Stack gap="sm">
              <Group gap="md" align="center">
                <Skeleton height={48} circle />
                <div style={{ flex: 1 }}>
                  <Skeleton height={12} width="40%" mb={8} />
                  <Skeleton height={10} width="60%" />
                </div>
              </Group>
              <Divider />
              <Skeleton height={12} width="30%" />
              <Skeleton height={12} width="70%" />
              <Skeleton height={12} width="50%" />
              <Group gap="md" mt="sm">
                <Skeleton height={10} width="40%" />
                <Skeleton height={10} width="30%" />
              </Group>
            </Stack>
          ) : (
            <Stack gap="sm">
              <Group gap="md" align="center">
                <Avatar size="lg" color="brand" radius="md">
                  {(profileData.firstName || profileData.email || "?").charAt(
                    0,
                  )}
                </Avatar>
                <div>
                  <Text fw={700} size="xl">
                    {[profileData.firstName, profileData.lastName]
                      .filter(Boolean)
                      .join(" ") || profileData.email}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {profileData.email}
                  </Text>
                </div>
              </Group>
              <Divider />
              <Group gap="md">
                <div>
                  <Text size="xs" c="dimmed">
                    Phone
                  </Text>
                  <Text size="sm">{profileData.phoneNumber || "—"}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Status
                  </Text>
                  {getStatusBadge(getStatus(profileData))}
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Monetization
                  </Text>
                  <Group gap={4}>
                    <Badge color={profileData.monetizationStatus === 'monetized' ? 'green' : profileData.monetizationStatus === 'eligible' ? 'blue' : 'gray'} variant="light">
                      {(profileData.monetizationStatus || 'None').replace('_', ' ').toUpperCase()}
                    </Badge>
                  </Group>
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Level
                  </Text>
                  <Badge color="yellow" variant="light">
                    Level {profileData.level ?? 1}
                  </Badge>
                </div>
              </Group>
              <Group gap="md">
                <div>
                  <Text size="xs" c="dimmed">
                    Verifications
                  </Text>
                  <Group gap={8} mt={4}>
                    <Badge 
                      color={profileData.isPhoneVerified ? 'green' : 'gray'} 
                      variant="dot" 
                      size="sm"
                    >
                      Phone {profileData.isPhoneVerified ? 'Verified' : 'Unverified'}
                    </Badge>

                  </Group>
                </div>
              </Group>
              <Group gap="md">
                <div>
                  <Text size="xs" c="dimmed">
                    Created
                  </Text>
                  <Text size="sm">{formatDate(profileData.createdAt)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Last Login
                  </Text>
                  <Text size="sm">{formatDate(profileData.lastLoginAt)}</Text>
                </div>
              </Group>
              <Divider />

              <Text fw={600} size="sm">
                Bank Account Details
              </Text>

              {profileData.bankAccount ? (
                <Paper p="sm" withBorder bg="var(--bg-surface-2)">
                  <Group grow>
                    <div>
                      <Text size="xs" c="dimmed">
                        Bank Name
                      </Text>
                      <Text size="sm" fw={500}>
                        {profileData.bankAccount.bankName}
                      </Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">
                        Account Number
                      </Text>
                      <Text size="sm" fw={500}>
                        {profileData.bankAccount.accountNumber}
                      </Text>
                    </div>
                  </Group>
                  <div style={{ marginTop: 8 }}>
                    <Text size="xs" c="dimmed">
                      Account Name
                    </Text>
                    <Text size="sm" fw={500}>
                      {profileData.bankAccount.accountName}
                    </Text>
                  </div>
                </Paper>
              ) : (
                <Text size="sm" c="dimmed" fs="italic">
                  No bank account linked
                </Text>
              )}
            </Stack>
          )}
          {profileData && (
            <Group justify="center" mt="md">
              <Button
                variant="outline"
                fullWidth
                onClick={() => navigate(`/d/users/${profileData.id}`)}
              >
                View Full Profile
              </Button>
            </Group>
          )}
        </Modal>

        <Modal
          opened={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setEditUserId(null);
            setEditFirstName("");
            setEditLastName("");
            setEditPhoneNumber("");
            setEditIsEmailVerified(null);
          }}
          title="Edit User"
          size="lg"
          centered
        >
          <Stack gap="md">
            <TextInput
              label="First Name"
              value={editFirstName}
              onChange={(e) => setEditFirstName(e.currentTarget.value)}
            />
            <TextInput
              label="Last Name"
              value={editLastName}
              onChange={(e) => setEditLastName(e.currentTarget.value)}
            />
            <TextInput
              label="Phone"
              value={editPhoneNumber}
              onChange={(e) => setEditPhoneNumber(e.currentTarget.value)}
            />

            <Select
              label="Email Verified"
              data={[
                { value: "keep", label: "Keep as-is" },
                { value: "true", label: "Verified" },
                { value: "false", label: "Not verified" },
              ]}
              value={
                editIsEmailVerified === null
                  ? "keep"
                  : String(editIsEmailVerified)
              }
              onChange={(v) => {
                if (v === "keep" || v === null) {
                  setEditIsEmailVerified(null);
                  return;
                }
                setEditIsEmailVerified(v === "true");
              }}
            />
            <Group justify="flex-end" gap="sm">
              <Button
                variant="default"
                onClick={() => {
                  setEditModalOpen(false);
                  setEditUserId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                loading={actionProcessing}
                disabled={!editUserId}
                onClick={async () => {
                  if (!editUserId) return;
                  setActionProcessing(true);
                  const endpoint = Endpoints.UpdateUser(editUserId);
                  await PerformRequest({
                    method: endpoint.method,
                    route: endpoint.route,
                    data: {
                      firstName: editFirstName,
                      lastName: editLastName,
                      phoneNumber: editPhoneNumber,
                      ...(editIsEmailVerified === null
                        ? {}
                        : { isEmailVerified: editIsEmailVerified }),
                    },
                  });
                  setActionProcessing(false);
                  setEditModalOpen(false);
                  await reloadData();
                }}
              >
                Save
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Modal
          opened={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setConfirmType(null);
            setConfirmUser(null);
            setConfirmReason("");
          }}
          title={
            confirmType === "delete"
              ? "Delete user"
              : confirmType === "suspend"
                ? "Suspend user"
                : "Confirm"
          }
          centered
        >
          <Stack gap="md">
            <Text size="sm">
              {confirmType === "delete"
                ? `This will permanently delete ${confirmUser?.email}. This action cannot be undone.`
                : `This will suspend ${confirmUser?.email}. They can still sign in, but withdrawals and completing opportunities will be blocked.`}
            </Text>
            <Textarea
              label={
                confirmType === "suspend"
                  ? "Reason for suspension"
                  : "Reason (optional)"
              }
              placeholder={
                confirmType === "suspend"
                  ? "Explain why this account is being suspended..."
                  : "Optional internal reason..."
              }
              minRows={3}
              required={confirmType === "suspend"}
              value={confirmReason}
              onChange={(e) => setConfirmReason(e.currentTarget.value)}
            />
            <Group justify="flex-end" gap="sm">
              <Button
                variant="default"
                onClick={() => {
                  setConfirmOpen(false);
                  setConfirmType(null);
                  setConfirmUser(null);
                  setConfirmReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                color="red"
                loading={actionProcessing}
                disabled={
                  !confirmUser ||
                  !confirmType ||
                  (confirmType === "suspend" && !confirmReason.trim())
                }
                onClick={async () => {
                  if (!confirmUser || !confirmType) return;
                  setActionProcessing(true);
                  if (confirmType === "suspend") {
                    const endpoint = Endpoints.SuspendUser(confirmUser.id);
                    await PerformRequest({
                      method: endpoint.method,
                      route: endpoint.route,
                      data: { reason: confirmReason.trim() },
                    });
                  }
                  if (confirmType === "delete") {
                    const endpoint = Endpoints.DeleteUser(confirmUser.id);
                    await PerformRequest({
                      method: endpoint.method,
                      route: endpoint.route,
                      data: confirmReason.trim()
                        ? { reason: confirmReason.trim() }
                        : undefined,
                    });
                  }
                  setActionProcessing(false);
                  setConfirmOpen(false);
                  setConfirmType(null);
                  setConfirmUser(null);
                  setConfirmReason("");
                  await reloadData();
                }}
              >
                {confirmType === "delete" ? "Delete" : "Suspend"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Onboard User Modal */}
        <Modal
          opened={onboardModalOpen}
          onClose={() => {
            setOnboardModalOpen(false);
            setOnboardEmail("");
            setOnboardPhone("");
            setOnboardFirstName("");
            setOnboardLastName("");
            setOnboardEmailError(null);
            setOnboardPhoneError(null);
          }}
          title="Onboard New User"
          centered
          size="md"
        >
          <Stack gap="md">
            <TextInput
              label="Email"
              placeholder="user@example.com"
              value={onboardEmail}
              onChange={(e) => {
                setOnboardEmail(e.currentTarget.value);
                setOnboardEmailError(null);
              }}
              required
              type="email"
              error={onboardEmailError}
            />
            <div>
              <Text size="sm" fw={500} mb={5}>
                Phone Number <span style={{ color: "red" }}>*</span>
              </Text>
              <Group gap={0} style={{ alignItems: "stretch" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor: "#F8F9FA",
                    border: "1px solid #DEE2E6",
                    borderRight: "none",
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                    padding: "0 12px",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  <span style={{ fontSize: "18px" }}>🇳🇬</span>
                  <span>+234</span>
                </div>
                <TextInput
                  placeholder="08012345678"
                  value={onboardPhone}
                  onChange={(e) => {
                    handlePhoneChange(e.currentTarget.value);
                    setOnboardPhoneError(null);
                  }}
                  required
                  style={{ flex: 1 }}
                  styles={{
                    input: {
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      borderLeft: "none",
                    },
                  }}
                  maxLength={11}
                  error={onboardPhoneError}
                />
              </Group>
              {!onboardPhoneError && (
                <Text size="xs" c="dimmed" mt={5}>
                  Nigerian phone number (11 digits, e.g., 08012345678)
                </Text>
              )}
            </div>
            <Group grow>
              <TextInput
                label="First Name"
                placeholder="John"
                value={onboardFirstName}
                onChange={(e) => setOnboardFirstName(e.currentTarget.value)}
              />
              <TextInput
                label="Last Name"
                placeholder="Doe"
                value={onboardLastName}
                onChange={(e) => setOnboardLastName(e.currentTarget.value)}
              />
            </Group>
            <Group justify="flex-end" gap="sm" mt="md">
              <Button
                variant="default"
                onClick={() => {
                  setOnboardModalOpen(false);
                  setOnboardEmail("");
                  setOnboardPhone("");
                  setOnboardFirstName("");
                  setOnboardLastName("");
                }}
                disabled={isOnboarding}
              >
                Cancel
              </Button>
              <Button
                onClick={handleOnboard}
                loading={isOnboarding}
                disabled={!onboardEmail.trim() || !onboardPhone.trim()}
              >
                Onboard User
              </Button>
            </Group>
          </Stack>
        </Modal>
        <ExportModal
          opened={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          title="Export Users"
          type="users"
          additionalFilters={null}
          onExport={async (dateFilters) => {
            const token = localStorage.getItem("t");
            // Use GetUsersReport which we updated to support CSV format
            const endpoint = Endpoints.GetUsersReport({
              format: "csv",
              ...dateFilters,
            });

            const response = await axios.get(endpoint.route, {
              headers: { Authorization: `Bearer ${token}` },
              responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `users_export_${new Date().toISOString()}.csv`,
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
        />
      </div>
    </PermissionGuard>
  );
};

export default Users;
